<template>
  <div class="px-5 pt-4 pb-5 content-bg--1">
    <div class="d-inline-block pb-4">
      <v-select
        v-if="chartTypeDetailActive"
        v-model="chartTypeDetail"
        @change="$emit('changeChartTypeDetail', chartTypeDetail)"
        :items="typeDetails"
        :disabled="loading"
        item-value="value"
        item-text="title"
        height="34"
        class="content-bg--1 v-text-field--noBorder"
        hide-details
        dense
        solo
      >
        <template v-slot:selection="{ item }">
          <div class="ellipsis" role="button">{{ item.title }}</div>
        </template>
      </v-select>
    </div>

    <div id="chart-legends" class="pb-4"></div>
    <bar-chart
      v-if="chartdata && options && !loading"
      :data="chartdata"
      :options="options"
    ></bar-chart>
  </div>
</template>

<script>
import BarChart from "../common/BarChart.js";

export default {
  name: "StatisticsChart",

  components: {
    BarChart
  },

  props: {
    // данные графика
    chartdata: Object,

    // отображение легенды графика
    legend: {
      type: Boolean,
      default: false
    },

    // select со списком типов детализации графика
    chartTypeDetailActive: {
      type: Boolean,
      default: false
    },
    // значение детализации из родительских компонентов
    chartTypeDetailProp: {
      type: String,
      default: ""
    },
    // стостояние вида отображения графика (наложение bar-ов)
    unStacked: {
      type: Boolean,
      default: false
    },

    // статус загрузки данных
    loading: {
      type: Boolean,
      default: false
    }
  },

  watch: {
    // обновление значения детализации
    chartTypeDetailProp(value) {
      this.chartTypeDetail = value;
    },
    // отправка значения детализации в родительский компонент
    chartTypeDetail(value) {
      this.$emit("changeChartTypeDetail", value);
    }
  },

  computed: {
    // опции графика
    options() {
      let finalObject = {};
      if (this.chartdata.datasets[0]) {
        // считаем к-во элементов, чтобы установить ширину полоски, если их мало
        let ctnData =
            this.chartdata.datasets[1] && this.chartdata.datasets[1].data.length
              ? this.chartdata.datasets[1].data.length
              : this.chartdata.datasets[0].data.length,
          // параметры по шкале x
          xAxes = {
            ticks: {
              // цвет labels
              fontColor: this.$store.getters.themeDark ? "#ffffff" : "#2F353D"
            },
            gridLines: {
              offsetGridLines: false,
              color: this.$store.getters.themeDark ? "#235087" : "#E0EDFC"
            },
            stacked: this.unStacked ? false : true
          };
        if (
          (!["sm", "xs"].includes(this.$vuetify.breakpoint.name) &&
            ctnData < 20) ||
          (["sm", "xs"].includes(this.$vuetify.breakpoint.name) && ctnData < 10)
        ) {
          // толщина полосы, чтобы не были слишком широкие, когда мало
          xAxes.barThickness = 30;
        }

        finalObject = {
          responsive: true,
          maintainAspectRatio: false,
          tooltips: this.chartdata.datasets[1]
            ? {
                mode: "label",
                callbacks: {
                  title: function() {},
                  label: function(tooltipItem, data) {
                    return tooltipItem.yLabel !== 0
                      ? `${
                          data.labels[tooltipItem.datasetIndex][
                            tooltipItem.index
                          ]
                        }: ${tooltipItem.yLabel}`
                      : "";
                  }
                }
              }
            : {},
          scales: {
            xAxes: !this.unStacked
              ? [xAxes]
              : [
                  {
                    id: "xAxisFirstID",
                    display: true,
                    color: "#ffffff",
                    fontColor: "#125165",
                    position: "top",
                    labels: this.chartdata.labels[0],
                    offset: true,
                    ticks: {
                      fontColor: this.$store.getters.themeDark
                        ? "#ffffff"
                        : "#2F353D"
                    },
                    gridLines: {
                      offsetGridLines: false,
                      color: this.$store.getters.themeDark
                        ? "#235087"
                        : "#E0EDFC"
                    }
                  },
                  {
                    id: "xAxisSecondID",
                    display: true,
                    position: "bottom",
                    labels: this.chartdata.labels[1],
                    offset: true,
                    ticks: {
                      fontColor: this.$store.getters.themeDark
                        ? "#ffffff"
                        : "#2F353D"
                    },
                    gridLines: {
                      offsetGridLines: false,
                      color: this.$store.getters.themeDark
                        ? "#235087"
                        : "#E0EDFC"
                    }
                  }
                ],
            yAxes: [
              {
                gridLines: {
                  color: this.$store.getters.themeDark ? "#235087" : "#E0EDFC"
                },
                ticks: {
                  beginAtZero: true,
                  // цвет labels
                  fontColor: this.$store.getters.themeDark
                    ? "#ffffff"
                    : "#2F353D"
                }
              }
            ]
          },
          elements: {
            rectangle: {
              borderWidth: 0
            }
          },
          legend: {
            display: false
          },
          customLegendDisplay: this.legend,
          legendCallback: function(chart) {
            let text = [];
            text.push(
              '<ul class="layout justify-end ' + chart.id + '-legend">'
            );
            for (let i = 0; i < chart.data.datasets.length; i++) {
              text.push(
                `
               <li class="pr-3 d-flex align-center">
                <span class="mr-2 chart-badge" style="background-color: ${
                  chart.data.datasets[i].backgroundColor
                }"></span>
                <span class="ml-1 font-weight-medium">${
                  chart.data.datasets[i].label
                    ? chart.data.datasets[i].label
                    : ""
                }</span>
               </li>
              `
              );
            }
            text.push("</ul>");
            return text.join("");
          }
        };
      }

      return finalObject;
    }
  },

  data: () => ({
    // тип детализации графика
    chartTypeDetail: "day",
    // список типов детализации
    typeDetails: [
      {
        title: "Детализация: по дням",
        value: "day"
      },
      {
        title: "Детализация: по неделям",
        value: "week"
      },
      {
        title: "Детализация: по месяцам",
        value: "month"
      },
      {
        title: "Детализация: по годам",
        value: "year"
      }
    ]
  }),

  mounted() {
    if (this.chartTypeDetailProp) {
      this.chartTypeDetail = this.chartTypeDetailProp;
    }
  },

  methods: {}
};
</script>
